import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, updateProfile, onAuthStateChanged } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { auth, db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { Student } from '../types';

const LISANS_PROGRAMS = [
  'Adli Bilişim Mühendisliği',
  'Bilgisayar Mühendisliği',
  'Kalite Mühendisliği',
  'Çevre Mühendisliği',
  'Elektrik ve Elektronik Mühendisliği',
  'Endüstri Mühendisliği',
  'Gemi İnşaası ve Gemi Makine Mühendisliği',
  'Gıda Mühendisliği',
  'İnşaat Mühendisliği',
  'Mekatronik Mühendisliği',
  'Siber Güvenlik Mühendisliği',
  'Yapay Zeka Mühendisliği',
  'Yazılım Mühendisliği',
  'Psikoloji',
  'Sosyoloji',
  'Psikolojik Danışmanlık ve Rehberlik',
  'Uluslararası Hukuk',
  'Bankacılık ve Sigorta',
  'Ekonomi',
  'İşletme Yönetimi',
  'Uluslararası İlişkiler',
  'Uluslararası Ticaret ve Finans',
  'Lojistik',
  'Mimarlık',
  'İç Mimarlık',
  'Sağlık Yönetimi',
  'Beslenme ve Diyetetik',
  'Çocuk Gelişimi',
  'İş Sağlığı ve Güvenliği',
  'Fizyoterapi ve Rehabilitasyon',
  'Spor Yönetimi ve Antrenörlüğü',
];

const LISANSUSTU_PROGRAMS = [
  'Adli Bilimler',
  'Aile Danışmanlığı',
  'Beslenme ve Diyetetik',
  'Bilgisayar Mühendisliği',
  'Bilişim Hukuku',
  'Biyomedikal Mühendisliği',
  'Çevre Mühendisliği',
  'Çocuk Gelişimi',
  'Davranış Bilimleri',
  'Elektrik - Elektronik Mühendisliği',
  'Endüstri Mühendisliği',
  'İç Mimarlık',
  'İklim Değişikliği ve Sürdürülebilir Kalkınma',
  'İktisat',
  'İnsan Kaynakları Yönetimi',
  'İnşaat Mühendisliği',
  'İş Sağlığı ve Güvenliği',
  'İşletme Yönetimi',
  'Kamu Hukuku',
  'Kamu Yönetimi',
  'Kimya Mühendisliği',
  'Klinik Psikoloji',
  'Lojistik ve Tedarik Zinciri Yönetimi',
  'Makine Mühendisliği',
  'Mekatronik Mühendisliği',
  'Mimarlık',
  'Muhasebe ve Finansman',
  'Özel Hukuk',
  'Psikolojik Danışmanlık ve Rehberlik',
  'Fizyoterapi ve Rehabilitasyon',
  'Sağlık Kurumları İşletmeciliği',
  'Sağlık Yönetimi',
  'Siyaset Bilimi ve Uluslararası İlişkiler',
  'Sosyoloji',
  'Spor Bilimleri - Atletik Performans',
  'Ticaret Hukuku',
  'Uluslararası Hukuk',
  'Uluslararası Ticaret ve Lojistik',
  'Yazılım Mühendisliği',
];

const today = new Date().toISOString().split('T')[0];

const StudentRegister: React.FC = () => {
  const [form, setForm] = useState({
    fullName: '',
    studentId: '',
    email: '',
    program: '',
    degree: 'bachelor',
    registrationDate: today,
    password: '',
    passwordConfirm: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (u) navigate('/student/portal', { replace: true });
    });
    return unsub;
  }, [navigate]);

  const set_ = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setError('');
  };

  const programs = form.degree === 'bachelor' ? LISANS_PROGRAMS : LISANSUSTU_PROGRAMS;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError('Şifreler eşleşmiyor.');
      return;
    }
    if (!form.program) {
      setError('Lütfen bir program seçin.');
      return;
    }

    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(cred.user, { displayName: form.fullName });

      const student: Student = {
        uid: cred.user.uid,
        fullName: form.fullName,
        studentId: form.studentId,
        email: form.email,
        program: form.program,
        degree: form.degree,
        createdAt: new Date(form.registrationDate).getTime(),
      };
      await set(ref(db, `students/${cred.user.uid}`), student);

      navigate('/student/portal', { replace: true });
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setError('Bu e-posta adresi zaten kullanılıyor.');
      } else if (code === 'auth/invalid-email') {
        setError('Geçersiz e-posta adresi.');
      } else if (code === 'auth/weak-password') {
        setError('Şifre çok zayıf. Daha güçlü bir şifre seçin.');
      } else if (code === 'auth/operation-not-allowed') {
        setError('E-posta/şifre girişi henüz aktif edilmemiş. Firebase Console → Authentication → Sign-in method → Email/Password etkinleştirin.');
      } else {
        setError(`Kayıt oluşturulamadı: ${code || 'Bilinmeyen hata'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="st-auth-bg">
      <div className="st-auth-card st-auth-card-wide">
        <div className="st-auth-logo">
          <div className="st-auth-seal">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2" fill="none"/>
              <path d="M24 8L26.5 15.5H34.5L28 20L30.5 27.5L24 23L17.5 27.5L20 20L13.5 15.5H21.5L24 8Z" fill="currentColor"/>
              <circle cx="24" cy="32" r="4" fill="currentColor"/>
            </svg>
          </div>
          <h1>University of American Atlantic</h1>
          <p>Öğrenci Bilgi Sistemi</p>
        </div>

        <div className="st-auth-divider" />

        <h2 className="st-auth-title">Öğrenci Kaydı</h2>

        <form onSubmit={handleSubmit} className="st-auth-form">
          <div className="st-form-row">
            <div className="st-form-group">
              <label>Ad Soyad</label>
              <input
                type="text"
                value={form.fullName}
                onChange={set_('fullName')}
                placeholder="Adınız Soyadınız"
                required
              />
            </div>
            <div className="st-form-group">
              <label>Öğrenci Numarası</label>
              <input
                type="text"
                value={form.studentId}
                onChange={set_('studentId')}
                placeholder="Örn: 2025010001"
                required
              />
            </div>
          </div>

          <div className="st-form-row">
            <div className="st-form-group">
              <label>E-posta Adresi</label>
              <input
                type="email"
                value={form.email}
                onChange={set_('email')}
                placeholder="ornek@uaa.edu"
                required
              />
            </div>
            <div className="st-form-group">
              <label>Kayıt Tarihi</label>
              <input
                type="date"
                value={form.registrationDate}
                onChange={set_('registrationDate')}
                required
              />
            </div>
          </div>

          <div className="st-form-row">
            <div className="st-form-group">
              <label>Öğrenim Düzeyi</label>
              <select value={form.degree} onChange={e => { set_('degree')(e); setForm(f => ({ ...f, program: '', degree: e.target.value })); }}>
                <option value="bachelor">Lisans</option>
                <option value="master">Yüksek Lisans</option>
                <option value="doctorate">Doktora</option>
              </select>
            </div>
            <div className="st-form-group">
              <label>Program / Bölüm</label>
              <select value={form.program} onChange={set_('program')} required>
                <option value="">Bölüm Seçin</option>
                {programs.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="st-form-row">
            <div className="st-form-group">
              <label>Şifre</label>
              <input
                type="password"
                value={form.password}
                onChange={set_('password')}
                placeholder="En az 6 karakter"
                required
              />
            </div>
            <div className="st-form-group">
              <label>Şifre Tekrar</label>
              <input
                type="password"
                value={form.passwordConfirm}
                onChange={set_('passwordConfirm')}
                placeholder="Şifrenizi tekrar girin"
                required
              />
            </div>
          </div>

          {error && <div className="st-auth-error">{error}</div>}

          <button type="submit" className="st-auth-btn" disabled={loading}>
            {loading ? 'Kayıt oluşturuluyor...' : 'Kayıt Ol'}
          </button>
        </form>

        <p className="st-auth-footer">
          Zaten hesabınız var mı?{' '}
          <Link to="/student/login" className="st-auth-link">Giriş Yap</Link>
        </p>
      </div>
    </div>
  );
};

export default StudentRegister;
